/**
 * @name pageDesigner
 *
 * @description
 * Contains all utilities about page designer mapping data that can be used in different components.
 */
import type {Page, LinkObject, SuggestionsColumn, ProductSuggestions, CLDImageAsset} from '_types'
import {createRedirectUrl} from '_utils'

// Flat shape produced by mapPageDesignerComponent for textItem/bannerNewsItem links (used by the PDP's packaging/shipment/return regions).
export interface PageContentLink {
    type?: 'text' | 'button'
    title?: string
    link?: string
}

// Flat shape produced by mapPageDesignerComponent for textItem/bannerNewsItem components.
export interface PageContentTextComponent {
    type: 'text' | 'button'
    id?: string
    name?: string
    text?: string
    links?: PageContentLink[]
}

// Flat shape produced by mapPageDesignerComponent for imageItem components.
export interface PageContentImageComponent {
    type: 'image'
    id?: string
    name?: string
    imageDesktop: CLDImageAsset
    imageTablet: CLDImageAsset
    imageMobile: CLDImageAsset
}

// Union of the component shapes a textBox/imageBox/PWA_packaging_pdp's `items` can hold (packaging/resi/spedizione regions).
export type PageContentComponent = PageContentTextComponent | PageContentImageComponent

/**
 * Maps the page designer data of search section.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} obj - An object containing ideas, women and men informations.
 * @returns {SuggestionsColumn} obj.ideasData - An object containing the title of the ideas column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.womenData - An object containing the title of the women column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.menData - An object containing the title of the men column and relative suggestion items.
 * @returns {ProductSuggestions} obj.productsData - An object containing the title of the products box and relative product items.
 */
export const mapSearchSectionPageDesignerData = (
    linkObject: LinkObject,
    pageComponents?: Page,
): {ideasData?: SuggestionsColumn; womenData?: SuggestionsColumn; menData?: SuggestionsColumn; productsData?: ProductSuggestions} => {
    const components = pageComponents?.regions?.[0]?.components

    let ideasData: SuggestionsColumn | undefined
    let womenData: SuggestionsColumn | undefined
    let menData: SuggestionsColumn | undefined
    let productsData: ProductSuggestions | undefined

    if (components) {
        const ideas = components[0]

        if (ideas?.data?.title) {
            ideasData = {
                title: {name: ideas?.data?.title, href: ideas?.data.link},
                items:
                    ideas?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const women = components[1]

        if (women?.data?.title) {
            womenData = {
                title: {name: women?.data?.title, href: women?.data.link},
                items:
                    women?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const men = components[2]

        if (men?.data?.title) {
            menData = {
                title: {name: men?.data?.title, href: men?.data.link},
                items:
                    men?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const products = components[3]

        if (products?.data?.title) {
            productsData = {
                title: products?.data?.title,
                productIds:
                    products?.regions?.[0]?.components?.filter((component) => component.data?.product)?.map((component) => component.data?.product) ?? [],
            }
        }
    }

    return {
        ideasData,
        womenData,
        menData,
        productsData,
    }
}

// Stable key fallback when component.id is missing.
export const stableKeyFromComponent = (component: any): string => {
    if (!component) return ''
    try {
        const typeId = component.typeId ?? ''
        const name = component.name ?? ''
        const data = component.data ?? {}
        const regions = component.regions ?? []
        return JSON.stringify({typeId, name, data, regions})
    } catch (_error) {
        return ''
    }
}

/**
 * @name mapPageDesignerComponent
 *
 * @description
 * Maps a single Page Designer component (from a region's `components` array) into the
 * flat shape consumed by the PDP (product-details) page content. Returns `undefined`
 * for component types that aren't handled yet.
 *
 * @param {any} subC - The raw Page Designer component.
 * @returns {any} The mapped component, or an array of mapped components for box-like types (textBox/imageBox).
 */
const mapPageDesignerComponent = (subC: any): any => {
     if (subC.typeId === 'commerce_assets.PWA_packaging_pdp') {
        return {
            type: 'PWA_packaging_pdp',
            id: subC.id,
            name: subC.name,
            // Sub-components can be either textItem or imageItem, so delegate to the same mapper used for those types.
            items:
                subC.regions
                    ?.flatMap((region) => region.components ?? [])
                    .map(mapPageDesignerComponent)
                    .filter(Boolean) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.PWA_sizeGuide_measurements_assets') {
        return {
            type: 'PWA_sizeGuide_measurements_assets',
            id: subC.id,
            name: subC.name,
            title: subC.data?.title,
            description: subC.data?.description,
            imageDesktop: subC.data?.cld_image_desktop,
            imageTablet: subC.data?.cld_image_tablet,
            imageMobile: subC.data?.cld_image_mobile,
            items:
                subC.regions
                    ?.flatMap((textBoxRegion) => textBoxRegion.components ?? [])
                    .map((textItem) => ({
                        type: 'PWA_sizeGuide_measurements_assets_item',
                        id: textItem.id,
                        name: textItem.name,
                        title: textItem.data?.title,
                        description: textItem.data?.description,
                    })) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.textBox') {
        return {
            type: 'textBox',
            id: subC.id,
            name: subC.name,
            // Children aren't necessarily textItem: delegate to the generic mapper to support any sub-component type.
            items:
                subC.regions
                    ?.flatMap((textBoxRegion) => textBoxRegion.components ?? [])
                    .map(mapPageDesignerComponent)
                    .filter(Boolean) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.textItem') {
        return {
            type: 'text',
            id: subC.id,
            name: subC.name,
            text: subC.data?.text,
            links: subC.regions
                ?.find((r) => r.id === 'links')
                ?.components?.map(
                    (link): PageContentLink => ({
                        title: link.data?.title,
                        link: link.data?.link,
                    }),
                ),
        } as PageContentTextComponent
    }
    if (subC.typeId === 'commerce_assets.bannerNewsItem') {
        const result: PageContentTextComponent = {
            type: 'text',
            id: subC.id,
            text: subC.data?.text,
            name: subC.name,
            links: subC.regions
                ?.find((r) => r.id === 'cta')
                ?.components?.map(
                    (link): PageContentLink => ({
                        type: link.data?.cta_type === 'button' ? 'button' : 'text',
                        title: link.data?.label_cta,
                        link: link.data?.link_cta,
                    }),
                ),
        }

        if (result.text === '{0}' && result.links?.[0]?.type === 'button') {
            result.type = 'button'
        }

        return result
    }
    if (subC.typeId === 'commerce_assets.imageBox') {
        return {
            type: 'imageBox',
            id: subC.id,
            name: subC.name,
            items:
                subC.regions
                    ?.flatMap((imageBoxRegion) => imageBoxRegion.components ?? [])
                    .map((imageItem) => ({
                        type: 'image',
                        id: imageItem.id,
                        name: imageItem.name,
                        imageDesktop: imageItem.data?.cld_image_desktop,
                        imageTablet: imageItem.data?.cld_image_tablet,
                        imageMobile: imageItem.data?.cld_image_mobile,
                    })) ?? [],
        }
    }
    if (subC.typeId === 'commerce_assets.imageItem') {
        return {
            type: 'image',
            imageDesktop: subC.data?.cld_image_desktop,
            imageTablet: subC.data?.cld_image_tablet,
            imageMobile: subC.data?.cld_image_mobile,
            name: subC.name,
        }
    }

    return undefined
}

/**
 * @name mapProductDetailsPageContent
 *
 * @description
 * Maps the PDP's Page Designer regions into a `{[regionId]: components[]}` object,
 * flattening each region's components via `mapPageDesignerComponent`.
 *
 * @param {Page} [pageDesignerData] - The Page Designer page data for the PDP.
 * @returns {Record<string, any[]>} The mapped page content, keyed by region id.
 */
export const mapProductDetailsPageContent = (pageDesignerData?: Page): Record<string, any[]> =>
    Object.fromEntries(pageDesignerData?.regions?.map((region) => [region.id, region.components?.flatMap(mapPageDesignerComponent) ?? []]) ?? [])

